import { Component, Input, OnInit } from '@angular/core';
import { AppKey, AppPartnerService } from '@galaxy/marketplace-apps';
import { Edition, EditionIdentifiers, EditionsService, MarketplaceAppService } from '@galaxy/marketplace-apps/v1';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Product } from '../../../shared/product';
import { MarketingType, PersonaType } from '../../product-details-v2.interface';

export interface RequiredProduct {
  name: string;
  productId: string;
  editionName?: string;
  displayName: string;
  redirectURL: string;
}

export type RequiredEdition = Pick<Edition, 'appId' | 'editionId' | 'name'>;

export interface RequiredProductNamePair {
  productId: string;
  name: string;
}

type RequiredAppDescription = Pick<Edition, 'appId' | 'editionId' | 'name'> & {
  redirectURL: string;
  isEdition: boolean;
};

@Component({
  selector: 'store-required-products-list',
  templateUrl: './required-products-list.component.html',
  styleUrls: ['./required-products-list.component.scss'],
  standalone: false,
})
export class RequiredProductsListComponent implements OnInit {
  @Input() product: Product;
  @Input() marketingType: MarketingType = MarketingType.Partner;
  @Input() personaType: PersonaType = PersonaType.Partner;
  @Input() partnerId: string;
  @Input() marketId: string;

  requiredAppDescriptions$: Observable<RequiredAppDescription[]>;

  PersonaType = PersonaType;

  constructor(
    private readonly marketplaceAppsService: MarketplaceAppService,
    private readonly appPartnerService: AppPartnerService,
    private editionService: EditionsService,
  ) {}

  ngOnInit(): void {
    const requiredAppIds = this.product?.restrictions?.appRequirement?.rules[0]?.appIds ?? [];
    const requiredEditionIdentifiers: EditionIdentifiers[] =
      this.product?.restrictions?.appRequirement?.rules[0]?.requiredEditions ?? [];
    const requiredAppAppKeys = requiredAppIds.map((appId) => new AppKey({ appId: appId }));
    const requiredEditionAppKeys = requiredEditionIdentifiers.map(
      (editionIdentifier) =>
        new AppKey({
          appId: editionIdentifier.appId,
          editionId: editionIdentifier.editionId ?? '',
        }),
    );
    const allRequiredAppKeys = requiredAppAppKeys.concat(requiredEditionAppKeys);

    if (this.marketingType === MarketingType.EndUser && allRequiredAppKeys.length > 0) {
      // Business App
      this.requiredAppDescriptions$ = this.appPartnerService
        .getMulti(allRequiredAppKeys, this.partnerId, this.marketId ?? 'default', null, false)
        .pipe(
          map((apps) => {
            const requiredAppDescriptions: RequiredAppDescription[] = [];
            for (let i = 0; i < allRequiredAppKeys.length; i++) {
              const appKey = allRequiredAppKeys[i];
              const app = apps.find((app) => appKey.appId === app?.key?.appId);
              if (!app) {
                // if the required app is not returned from the getMulti that means it is not currently being sold
                // and should not be displayed, so exclude it from this list
                continue;
              }
              const isEdition = appKey.editionId == '' || !!appKey.editionId;
              let name = app.sharedMarketingInformation.name;
              if (isEdition) {
                name += ' | ' + app.sharedMarketingInformation.editionName;
              }
              const description: RequiredAppDescription = {
                appId: appKey.appId,
                editionId: appKey.editionId,
                isEdition: appKey.editionId == '' || !!appKey.editionId,
                name: name,
                redirectURL: this.redirectURL(appKey.appId),
              };
              requiredAppDescriptions.push(description);
            }
            return requiredAppDescriptions;
          }),
        );
    } else if (this.marketingType === MarketingType.Partner && allRequiredAppKeys.length > 0) {
      // Partner Center
      let requiredEditions$: Observable<RequiredEdition[]> = of([]);
      if (requiredEditionIdentifiers && requiredEditionIdentifiers.length > 0) {
        requiredEditions$ = this.editionService.getMultiEditionForMultiApp(requiredEditionIdentifiers).pipe(
          map((editions) =>
            editions.map((edition) => {
              return {
                appId: edition.appId,
                editionId: edition.editionId,
                name: edition.name,
              };
            }),
          ),
        );
      }
      const apps$ = this.marketplaceAppsService.getMultiApps(allRequiredAppKeys.map((ak) => ak.appId));
      this.requiredAppDescriptions$ = combineLatest([apps$, requiredEditions$]).pipe(
        map(([apps, editions]) => {
          return allRequiredAppKeys.map((appKey) => {
            const isEdition = appKey.editionId == '' || !!appKey.editionId;
            const app = apps.find((app) => appKey.appId === app.appId);
            let name = app.name;
            if (isEdition) {
              const editionName = editions.find(
                (edition) => edition.appId === appKey.appId && (edition.editionId ?? '') === (appKey.editionId ?? ''),
              )?.name;
              name += ' | ' + editionName;
            }
            const description: RequiredAppDescription = {
              appId: appKey.appId,
              editionId: appKey.editionId,
              isEdition: appKey.editionId == '' || !!appKey.editionId,
              name: name,
              redirectURL: this.redirectURL(appKey.appId),
            };
            return description;
          });
        }),
      );
    } else {
      // In case we get something unexpected, return empty array so as not to accidentally break whitelabel
      this.requiredAppDescriptions$ = of([]);
    }
  }

  redirectURL(appId: string): string {
    return this.personaType === PersonaType.Partner ? '/marketplace/products/' + appId : '../' + appId;
  }
}
